import * as React from "react"
import { GitBranch } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { buildCondition, type ConditionOperator } from "@/lib/message-template"

/**
 * Writes a condition into the message for you. It is not a builder that hides
 * the syntax: what it inserts is ordinary text, shown before it is inserted,
 * and stays editable in the message box afterwards.
 */
export function InsertConditionDialog({
  variables,
  onInsert,
}: {
  variables: string[]
  onInsert: (text: string) => void
}) {
  const [open, setOpen] = React.useState(false)
  const [field, setField] = React.useState(variables[0] ?? "")
  const [operator, setOperator] = React.useState<ConditionOperator>("=")
  const [value, setValue] = React.useState("")
  const [thenText, setThenText] = React.useState("")
  const [elseText, setElseText] = React.useState("")

  // The query can change underneath the dialog on the admin form.
  const chosenField = variables.includes(field) ? field : (variables[0] ?? "")
  const syntax = buildCondition({ field: chosenField, operator, value, thenText, elseText })
  const ready = chosenField !== "" && thenText !== ""

  const reset = () => {
    setOperator("=")
    setValue("")
    setThenText("")
    setElseText("")
  }

  const insert = () => {
    onInsert(syntax)
    setOpen(false)
    reset()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="sm">
          <GitBranch className="h-3.5 w-3.5" />
          Insert condition
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Insert a condition</DialogTitle>
          <DialogDescription>
            Say one thing to members who match and something else to everyone
            else, in the same send. It goes into the message as text you can
            still edit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="condition_field">Field</label>
              <Select
                id="condition_field"
                value={chosenField}
                onChange={(event) => setField(event.target.value)}
              >
                {variables.map((variable) => (
                  <option key={variable} value={variable}>
                    {`{${variable}}`}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <label htmlFor="condition_operator">Comparison</label>
              <Select
                id="condition_operator"
                value={operator}
                onChange={(event) => setOperator(event.target.value as ConditionOperator)}
              >
                <option value="=">is</option>
                <option value="!=">is not</option>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="condition_value">Value</label>
            <Input
              id="condition_value"
              value={value}
              placeholder="TEST EMPLOYER"
              onChange={(event) => setValue(event.target.value)}
              autoComplete="off"
            />
            <p className="text-xs text-ink-muted">
              Compared without regard to capital letters or spaces at either end.
              Leave it blank to match members with nothing in this field.
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="condition_then">When it matches, say</label>
            <Input
              id="condition_then"
              value={thenText}
              placeholder="Your dues are deducted automatically"
              onChange={(event) => setThenText(event.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="condition_else">Otherwise, say (optional)</label>
            <Input
              id="condition_else"
              value={elseText}
              placeholder="You need to pay dues directly to District Council 1"
              onChange={(event) => setElseText(event.target.value)}
              autoComplete="off"
            />
            <p className="text-xs text-ink-muted">
              Either wording can use variables like {"{first_name}"}. Leave this
              blank and members who don&rsquo;t match get nothing in its place.
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-ink-muted">This inserts:</p>
            <p className="callout break-all py-2 font-mono text-xs text-ink-display">{syntax}</p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" onClick={insert} disabled={!ready}>
            Insert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
